/**
 * 📋 Unified Work Service — Minha Mesa + Equipe
 *
 * Aggregates tasks from all platform sources into UnifiedWorkItem[]:
 * - Actions (plano de ação) from `actions` table
 * - Workflow tasks from `workflow_tasks_v9` table
 * - Problem investigations from `issues` table (active MASP stages)
 * - Monitoring tasks from `issues` table (monitoring/validation status)
 *
 * Rules:
 * - Always filter by organization_id
 * - Never call Supabase directly in components
 */

import { supabase } from '../../../lib/supabaseClient';
import { ActionService } from '../../../services/actionService';
import { getUserAssignments } from '../../../services/organizationStructureService';
import type {
  UnifiedWorkItem,
  WorkItemSource,
  WorkItemStatus,
  WorkDashboardKPIs,
  TeamMemberWorkload,
  TeamDashboardKPIs,
} from '../types/workItem';

// ─── Status Mapping ─────────────────────────────────────────

/** Map action DB statuses → unified statuses */
function mapActionStatus(dbStatus: string): WorkItemStatus {
  switch (dbStatus) {
    case 'pending': return 'pending';
    case 'in_progress': return 'in_progress';
    case 'completed': return 'completed';
    case 'cancelled': return 'completed';
    default: return 'pending';
  }
}

/** Map workflow task statuses → unified statuses */
function mapWorkflowStatus(status: string): WorkItemStatus {
  switch (status) {
    case 'pending': return 'pending';
    case 'in_progress': return 'in_progress';
    case 'paused': return 'in_progress';
    case 'completed': return 'completed';
    case 'failed': return 'waiting';
    default: return 'pending';
  }
}

/** Map issue statuses → unified statuses */
function mapIssueStatus(status: string): WorkItemStatus {
  switch (status) {
    case 'open': return 'pending';
    case 'investigation':
    case 'root_cause':
    case 'execution': return 'in_progress';
    case 'action_plan': return 'waiting';
    case 'validation':
    case 'monitoring': return 'in_progress';
    case 'closed': return 'completed';
    default: return 'pending';
  }
}

function hasUsableAssigneeName(item: Pick<UnifiedWorkItem, 'assigned_to' | 'assigned_to_name'>): boolean {
  const name = item.assigned_to_name?.trim();
  return Boolean(name && name !== item.assigned_to);
}

async function resolveAssigneeNames(
  organizationId: string,
  items: UnifiedWorkItem[],
): Promise<UnifiedWorkItem[]> {
  const unresolvedIds = [...new Set(
    items
      .filter((item) => item.assigned_to && !hasUsableAssigneeName(item))
      .map((item) => item.assigned_to.trim())
  )];

  if (unresolvedIds.length === 0) {
    return items;
  }

  const nameMap = new Map<string, string>();

  const { data: profiles, error: profileError } = await supabase
    .from('user_profiles')
    .select('user_id, full_name, name, email')
    .in('user_id', unresolvedIds);

  if (!profileError && profiles) {
    for (const row of profiles as Record<string, unknown>[]) {
      const userId = row.user_id as string | undefined;
      const resolvedName =
        (row.full_name as string | null) ??
        (row.name as string | null) ??
        (row.email as string | null) ??
        userId ??
        '';

      if (userId && resolvedName) {
        nameMap.set(userId, resolvedName);
      }
    }
  }

  const missingIds = unresolvedIds.filter((id) => !nameMap.has(id));

  if (missingIds.length > 0) {
    const { data: orgUsers } = await supabase
      .from('user_organizations')
      .select('user_id, users:user_id(email, raw_user_meta_data)')
      .eq('organization_id', organizationId)
      .in('user_id', missingIds);

    if (orgUsers) {
      for (const row of orgUsers as any[]) {
        const meta = row.users?.raw_user_meta_data ?? {};
        const resolvedName = meta.full_name || meta.name || row.users?.email || row.user_id;
        if (row.user_id && resolvedName) {
          nameMap.set(row.user_id, resolvedName);
        }
      }
    }
  }

  return items.map((item) => {
    if (!item.assigned_to || hasUsableAssigneeName(item)) {
      return item;
    }

    const resolvedName = nameMap.get(item.assigned_to);
    return resolvedName
      ? { ...item, assigned_to_name: resolvedName }
      : item;
  });
}

// ─── Fetch Actions ──────────────────────────────────────────

async function fetchActions(
  organizationId: string,
  userId?: string,
): Promise<UnifiedWorkItem[]> {
  if (userId) {
    await ActionService.promoteOrphansToCreator(organizationId, userId);
  }

  const actionColumns = 'id, title, description, status, due_date, assigned_to, created_by, priority, project_id, created_at, updated_at, organization_id, problem_id, metadata';
  let query = supabase
    .from('actions')
    .select(actionColumns)
    .eq('organization_id', organizationId)
    .neq('status', 'cancelled');

  if (userId) {
    query = query.or(`assigned_to.eq.${userId},created_by.eq.${userId}`);
  }

  let data: Array<Record<string, unknown>> | null = null;
  let error: { message: string } | null = null;
  const first = await query.limit(200);
  data = (first.data ?? null) as Array<Record<string, unknown>> | null;
  error = first.error;
  if (error && /column|schema cache|Could not find/i.test(error.message)) {
    let fallback = supabase
      .from('actions')
      .select('id, title, description, status, due_date, assigned_to, created_by, priority, project_id, created_at, updated_at, organization_id')
      .eq('organization_id', organizationId)
      .neq('status', 'cancelled');
    if (userId) {
      fallback = fallback.or(`assigned_to.eq.${userId},created_by.eq.${userId}`);
    }
    const retry = await fallback.limit(200);
    data = (retry.data ?? null) as Array<Record<string, unknown>> | null;
    error = retry.error;
  }
  if (error && userId) {
    const retry = await supabase
      .from('actions')
      .select('id, title, description, status, due_date, assigned_to, priority, project_id, created_at, updated_at, organization_id')
      .eq('organization_id', organizationId)
      .neq('status', 'cancelled')
      .eq('assigned_to', userId)
      .limit(200);
    data = (retry.data ?? null) as Array<Record<string, unknown>> | null;
    error = retry.error;
  }
  if (error || !data) return [];

  const now = new Date();
  return data.map((row: Record<string, unknown>) => {
    const status = mapActionStatus(row.status as string);
    const dueDate = row.due_date as string | null;
    const isOverdue = dueDate ? new Date(dueDate) < now && status !== 'completed' : false;
    const metadata = (row.metadata ?? null) as { problem_id?: string } | null;
    const problemId = (row.problem_id as string | null | undefined) ?? metadata?.problem_id ?? null;

    return {
      id: `action-${row.id}`,
      source: 'action' as WorkItemSource,
      source_id: row.id as string,
      title: row.title as string,
      description: (row.description as string) ?? null,
      status,
      assigned_to: (row.assigned_to as string) ?? '',
      priority: row.priority as string | null,
      due_date: dueDate,
      created_at: row.created_at as string,
      is_overdue: isOverdue,
      organization_id: row.organization_id as string,
      project_id: row.project_id as string | null,
      source_context: {
        action_status: row.status as string,
        problem_id: problemId,
        managerial_priority: (row.priority as string | null) ?? null,
        operational_signal: isOverdue ? 'Atraso' : null,
      },
    };
  });
}

// ─── Fetch Workflow Tasks ───────────────────────────────────

async function fetchWorkflowTasks(
  organizationId: string,
  userId?: string,
): Promise<UnifiedWorkItem[]> {
  // 1. Tasks explicitly assigned to this user
  let query = supabase
    .from('workflow_tasks_v9')
    .select(`
      id, instance_id, stage_id, status, assigned_to,
      sla_deadline, is_overdue, started_at, completed_at, created_at, updated_at,
      organization_id
    `)
    .eq('organization_id', organizationId)
    .in('status', ['pending', 'in_progress', 'paused', 'failed']);

  if (userId) {
    query = query.eq('assigned_to', userId);
  }

  const { data: directTasks, error } = await query.limit(200);

  // 2. Unclaimed role-queue tasks matching user's assignments (exact role+dept pairs)
  let roleQueueTasks: typeof directTasks = [];
  if (userId) {
    try {
      const assignments = await getUserAssignments(organizationId, userId);
      const roleIds = [...new Set(assignments.map(a => a.roleId))];

      if (roleIds.length > 0) {
        const { data: roleTasks } = await supabase
          .from('workflow_tasks_v9')
          .select(`
            id, instance_id, stage_id, status, assigned_to,
            sla_deadline, is_overdue, started_at, completed_at, created_at, updated_at,
            organization_id, assigned_role_id, assigned_department_id
          `)
          .eq('organization_id', organizationId)
          .in('status', ['pending'])
          .is('assigned_to', null)
          .in('assigned_role_id', roleIds)
          .limit(100);

        // Exact match: user must have assignment for (role_id + department_id)
        const assignmentPairs = new Set(assignments.map(a => `${a.roleId}:${a.departmentId}`));
        roleQueueTasks = (roleTasks ?? []).filter(t => {
          const taskRoleId = (t as Record<string, unknown>).assigned_role_id as string | null;
          const taskDeptId = (t as Record<string, unknown>).assigned_department_id as string | null;
          if (!taskRoleId) return false;
          if (!taskDeptId) return roleIds.includes(taskRoleId); // no dept constraint → role match enough
          return assignmentPairs.has(`${taskRoleId}:${taskDeptId}`);
        });
      }
    } catch { /* assignment lookup failed — proceed with direct tasks only */ }
  }

  // Merge and deduplicate
  const seenIds = new Set<string>();
  const data = [...(directTasks ?? []), ...(roleQueueTasks ?? [])].filter(t => {
    const id = t.id as string;
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  if (data.length === 0) return [];

  // Fetch instance details separately (avoids FK join requirement)
  const instanceIds = [...new Set(data.map(d => d.instance_id).filter(Boolean))];
  let instanceMap: Record<string, { title: string | null; reference_code: string | null; priority: string | null }> = {};
  if (instanceIds.length > 0) {
    const { data: instances } = await supabase
      .from('workflow_instances_v9')
      .select('id, title, reference_code, priority')
      .in('id', instanceIds);
    if (instances) {
      instanceMap = Object.fromEntries(instances.map(i => [i.id, { title: i.title, reference_code: i.reference_code, priority: i.priority }]));
    }
  }

  // Fetch stage names (stage_name doesn't exist on workflow_tasks_v9)
  const stageIds = [...new Set(data.map(d => d.stage_id).filter(Boolean))];
  let stageMap: Record<string, string> = {};
  if (stageIds.length > 0) {
    const { data: stages } = await supabase
      .from('workflow_stages_v9')
      .select('id, name')
      .in('id', stageIds);
    if (stages) {
      stageMap = Object.fromEntries(stages.map(s => [s.id, s.name]));
    }
  }

  return data.map((row: Record<string, unknown>) => {
    const pi = instanceMap[row.instance_id as string] ?? null;
    const status = mapWorkflowStatus(row.status as string);
    const instanceTitle = pi?.title as string | null;
    const stageName = stageMap[row.stage_id as string] ?? null;
    const title = instanceTitle
      ? `${instanceTitle} — ${stageName ?? 'Tarefa'}`
      : stageName ?? 'Tarefa de Workflow';

    return {
      id: `workflow-${row.id}`,
      source: 'workflow' as WorkItemSource,
      source_id: row.id as string,
      title,
      status,
      assigned_to: (row.assigned_to as string) ?? '',
      assigned_to_name: null,
      priority: (pi?.priority as string) ?? null,
      due_date: (row.sla_deadline as string) ?? null,
      started_at: (row.started_at as string) ?? null,
      completed_at: (row.completed_at as string) ?? null,
      created_at: row.created_at as string,
      is_overdue: (row.is_overdue as boolean) ?? false,
      organization_id: row.organization_id as string,
      source_context: {
        instance_title: instanceTitle ?? undefined,
        stage_name: stageName ?? undefined,
        sla_deadline: (row.sla_deadline as string) ?? null,
      },
    };
  });
}

// ─── Fetch Problem Investigations ───────────────────────────

async function fetchProblemTasks(
  organizationId: string,
  userId?: string,
): Promise<UnifiedWorkItem[]> {
  let query = supabase
    .from('issues')
    .select('id, title, description, type, severity, status, mode, assigned_to, project_id, due_date, created_at, updated_at, organization_id')
    .eq('organization_id', organizationId)
    .not('status', 'eq', 'closed');

  if (userId) {
    query = query.eq('assigned_to', userId);
  }

  const { data, error } = await query.limit(200);
  if (error || !data) return [];

  const now = new Date();

  // Split into problem & monitoring
  const items: UnifiedWorkItem[] = [];

  for (const row of data as Record<string, unknown>[]) {
    const issueStatus = row.status as string;
    const dueDate = row.due_date as string | null;
    const isOverdue = dueDate ? new Date(dueDate) < now : false;

    // Monitoring tasks = issues in validation or monitoring stage
    if (issueStatus === 'validation' || issueStatus === 'monitoring') {
      items.push({
        id: `monitoring-${row.id}`,
        source: 'monitoring' as WorkItemSource,
        source_id: row.id as string,
        title: `[Monitorar] ${row.title as string}`,
        description: (row.description as string) ?? null,
        status: 'in_progress',
        assigned_to: (row.assigned_to as string) ?? '',
        due_date: dueDate,
        created_at: row.created_at as string,
        is_overdue: isOverdue,
        organization_id: row.organization_id as string,
        project_id: row.project_id as string | null,
        source_context: {
          issue_severity: row.severity as string,
          issue_type: row.type as string,
          issue_mode: row.mode as string,
          issue_status: issueStatus,
          monitoring_mode: issueStatus === 'monitoring' ? 'monitoring' : 'validation',
        },
      });
    } else {
      // Problem investigation
      items.push({
        id: `problem-${row.id}`,
        source: 'problem' as WorkItemSource,
        source_id: row.id as string,
        title: row.title as string,
        description: (row.description as string) ?? null,
        status: mapIssueStatus(issueStatus),
        assigned_to: (row.assigned_to as string) ?? '',
        due_date: dueDate,
        created_at: row.created_at as string,
        is_overdue: isOverdue,
        organization_id: row.organization_id as string,
        project_id: row.project_id as string | null,
        source_context: {
          issue_severity: row.severity as string,
          issue_type: row.type as string,
          issue_mode: row.mode as string,
          issue_status: issueStatus,
        },
      });
    }
  }

  return items;
}

// ─── Completed Tasks (Today) ────────────────────────────────

async function fetchCompletedToday(
  organizationId: string,
  userId?: string,
): Promise<UnifiedWorkItem[]> {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayISO = todayStart.toISOString();

  const items: UnifiedWorkItem[] = [];

  // Completed actions today
  let aq = supabase
    .from('actions')
    .select('id, title, status, assigned_to, due_date, created_at, updated_at, organization_id, project_id')
    .eq('organization_id', organizationId)
    .eq('status', 'completed')
    .gte('updated_at', todayISO);
  if (userId) aq = aq.or(`assigned_to.eq.${userId},created_by.eq.${userId}`);
  const { data: actData } = await aq.limit(50);

  if (actData) {
    for (const row of actData as Record<string, unknown>[]) {
      items.push({
        id: `action-${row.id}`,
        source: 'action',
        source_id: row.id as string,
        title: row.title as string,
        status: 'completed',
        assigned_to: (row.assigned_to as string) ?? '',
        due_date: (row.due_date as string) ?? null,
        completed_at: row.updated_at as string,
        created_at: row.created_at as string,
        is_overdue: false,
        organization_id: row.organization_id as string,
        project_id: row.project_id as string | null,
      });
    }
  }

  // Completed workflow tasks today
  let wq = supabase
    .from('workflow_tasks_v9')
    .select('id, stage_name, status, assigned_to, assigned_to_name, completed_at, created_at, updated_at, organization_id')
    .eq('organization_id', organizationId)
    .eq('status', 'completed')
    .gte('completed_at', todayISO);
  if (userId) wq = wq.eq('assigned_to', userId);
  const { data: wfData } = await wq.limit(50);

  if (wfData) {
    for (const row of wfData as Record<string, unknown>[]) {
      items.push({
        id: `workflow-${row.id}`,
        source: 'workflow',
        source_id: row.id as string,
        title: (row.stage_name as string) ?? 'Tarefa de Workflow',
        status: 'completed',
        assigned_to: (row.assigned_to as string) ?? '',
        assigned_to_name: (row.assigned_to_name as string) ?? null,
        completed_at: (row.completed_at as string) ?? null,
        created_at: row.created_at as string,
        is_overdue: false,
        organization_id: row.organization_id as string,
      });
    }
  }

  return items;
}

// ─── Fetch Scoped Project Deliverables ──────────────────────
// Prompt 37 — Entregáveis dos projetos do usuário (actions com project_id
// dentro do escopo de membership). Inclui apenas itens não atribuídos ou
// atribuídos ao próprio usuário, para evitar contaminar a fila pessoal.

async function fetchScopedProjectActions(
  organizationId: string,
  userId: string,
): Promise<UnifiedWorkItem[]> {
  // 1. Projects where the user is a member
  const { data: memberships } = await supabase
    .from('project_members')
    .select('project_id')
    .eq('user_id', userId);

  const projectIds = [...new Set((memberships ?? []).map((m: Record<string, unknown>) => m.project_id as string))];
  if (projectIds.length === 0) return [];

  // 2. Actions in those projects — unclaimed OR assigned to user
  const { data, error } = await supabase
    .from('actions')
    .select('id, title, description, status, due_date, assigned_to, priority, project_id, created_at, updated_at, organization_id')
    .eq('organization_id', organizationId)
    .in('project_id', projectIds)
    .not('status', 'in', '(completed,cancelled)')
    .or(`assigned_to.is.null,assigned_to.eq.${userId}`)
    .limit(200);

  if (error || !data) return [];

  const now = new Date();
  return data.map((row: Record<string, unknown>) => {
    const status = mapActionStatus(row.status as string);
    const dueDate = row.due_date as string | null;
    const isOverdue = dueDate ? new Date(dueDate) < now && status !== 'completed' : false;

    return {
      id: `action-${row.id}`,
      source: 'action' as WorkItemSource,
      source_id: row.id as string,
      title: row.title as string,
      description: (row.description as string) ?? null,
      status,
      assigned_to: (row.assigned_to as string) ?? '',
      priority: row.priority as string | null,
      due_date: dueDate,
      created_at: row.created_at as string,
      is_overdue: isOverdue,
      organization_id: row.organization_id as string,
      project_id: row.project_id as string | null,
      source_context: {
        action_status: row.status as string,
      },
    };
  });
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Fetch ALL work items assigned to a user across all sources.
 *
 * Prompt 37 — Fontes unificadas:
 *  • Workflow tasks (workflow_tasks_v9) — diretas + role-queue
 *  • Planos de ação (actions) — assigned_to = userId
 *  • Entregáveis de projetos (actions com project_id em projects do usuário)
 *  • Ocorrências/Falhas (issues) — investigation + monitoring
 *  • Concluídos hoje (cross-source)
 */
export async function fetchMyWorkItems(
  organizationId: string,
  userId: string,
): Promise<UnifiedWorkItem[]> {
  const [actions, workflows, problems, scopedDeliverables, completed] = await Promise.all([
    fetchActions(organizationId, userId),
    fetchWorkflowTasks(organizationId, userId),
    fetchProblemTasks(organizationId, userId),
    fetchScopedProjectActions(organizationId, userId),
    fetchCompletedToday(organizationId, userId),
  ]);

  // Merge active + completed, dedup by id (later sources don't override)
  const activeMap = new Map<string, UnifiedWorkItem>();
  for (const item of [...actions, ...workflows, ...problems, ...scopedDeliverables]) {
    if (!activeMap.has(item.id)) activeMap.set(item.id, item);
  }
  for (const item of completed) {
    if (!activeMap.has(item.id)) activeMap.set(item.id, item);
  }

  return resolveAssigneeNames(organizationId, Array.from(activeMap.values()));
}

// ─── Fetch User's Projects ──────────────────────────────────

export interface MyProject {
  id: string;
  name: string;
  status: string;
  role: string;
  progress: number;
  open_actions: number;
  active_workflows: number;
}

export async function fetchMyProjects(
  organizationId: string,
  userId: string,
): Promise<MyProject[]> {
  // Get projects where user is a member
  const { data: memberships, error: memberError } = await supabase
    .from('project_members')
    .select('project_id, role')
    .eq('user_id', userId);

  if (memberError || !memberships || memberships.length === 0) return [];

  const projectIds = memberships.map(m => m.project_id);
  const roleMap = new Map(memberships.map(m => [m.project_id, m.role]));

  const { data: projects, error } = await supabase
    .from('projects')
    .select('id, name, status, progress')
    .eq('organization_id', organizationId)
    .in('id', projectIds)
    .in('status', ['active', 'draft', 'on_hold'])
    .order('updated_at', { ascending: false })
    .limit(20);

  if (error || !projects) return [];

  return projects.map(p => ({
    id: p.id,
    name: p.name ?? 'Sem nome',
    status: p.status ?? 'active',
    role: roleMap.get(p.id) ?? 'viewer',
    progress: Number(p.progress ?? 0),
    open_actions: 0,
    active_workflows: 0,
  }));
}

/**
 * Compute dashboard KPIs from a list of work items.
 */
export function computeKPIs(items: UnifiedWorkItem[]): WorkDashboardKPIs {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  return {
    tasks_in_progress: items.filter(i => i.status === 'in_progress').length,
    tasks_pending: items.filter(i => i.status === 'pending').length,
    tasks_overdue: items.filter(i => i.is_overdue && i.status !== 'completed').length,
    tasks_completed_today: items.filter(i => {
      if (i.status !== 'completed') return false;
      const dt = i.completed_at ?? i.created_at;
      return new Date(dt) >= todayStart;
    }).length,
  };
}

/**
 * Fetch all work items for a team (all org members).
 * Returns items grouped by user for team management.
 */
export async function fetchTeamWorkItems(
  organizationId: string,
): Promise<UnifiedWorkItem[]> {
  const [actions, workflows, problems, completed] = await Promise.all([
    fetchActions(organizationId),
    fetchWorkflowTasks(organizationId),
    fetchProblemTasks(organizationId),
    fetchCompletedToday(organizationId),
  ]);

  const allMap = new Map<string, UnifiedWorkItem>();
  for (const item of [...actions, ...workflows, ...problems]) {
    allMap.set(item.id, item);
  }
  for (const item of completed) {
    if (!allMap.has(item.id)) {
      allMap.set(item.id, item);
    }
  }

  return resolveAssigneeNames(organizationId, Array.from(allMap.values()));
}

/**
 * Compute team workload from unified items.
 */
export function computeTeamWorkload(items: UnifiedWorkItem[]): TeamMemberWorkload[] {
  const byUser = new Map<string, UnifiedWorkItem[]>();

  for (const item of items) {
    if (!item.assigned_to) continue;
    const list = byUser.get(item.assigned_to) ?? [];
    list.push(item);
    byUser.set(item.assigned_to, list);
  }

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const now = Date.now();

  const result: TeamMemberWorkload[] = [];
  for (const [userId, userItems] of byUser) {
    const inProgress = userItems.filter(i => i.status === 'in_progress').length;
    const pending = userItems.filter(i => i.status === 'pending').length;
    const overdue = userItems.filter(i => i.is_overdue && i.status !== 'completed').length;
    const completedToday = userItems.filter(i => {
      if (i.status !== 'completed') return false;
      const dt = i.completed_at ?? i.created_at;
      return new Date(dt) >= todayStart;
    }).length;

    // SLA computation
    const activeItems = userItems.filter(i => i.status !== 'completed');
    const withSla = activeItems.filter(i => i.source_context?.sla_deadline || i.due_date);
    const slaViolated = withSla.filter(i => {
      const deadline = i.source_context?.sla_deadline ?? i.due_date;
      return deadline && new Date(deadline).getTime() < now;
    });
    const slaViolatedPct = withSla.length > 0 ? Math.round((slaViolated.length / withSla.length) * 100) : 0;

    const slaMinutes = withSla.map(i => {
      const deadline = i.source_context?.sla_deadline ?? i.due_date;
      return deadline ? (new Date(deadline).getTime() - now) / 60000 : null;
    }).filter((v): v is number => v !== null);
    const avgSlaMins = slaMinutes.length > 0 ? slaMinutes.reduce((a, b) => a + b, 0) / slaMinutes.length : null;

    // Bottleneck score: weighted combination of overdue + load + avg idle time
    const totalActive = inProgress + pending;
    const avgIdleMs = activeItems.length > 0
      ? activeItems.reduce((sum, i) => sum + (now - new Date(i.started_at || i.created_at).getTime()), 0) / activeItems.length
      : 0;
    const bottleneckScore = (overdue * 3) + (totalActive * 1) + (avgIdleMs / (1000 * 60 * 60 * 24)); // days idle

    // Load status
    const loadStatus: 'normal' | 'high' | 'overloaded' =
      totalActive > 8 || overdue > 3 ? 'overloaded' :
      totalActive > 5 || overdue > 1 ? 'high' : 'normal';

    // Get the best available display name for this operator
    const nameItem = userItems.find((i) => hasUsableAssigneeName(i));
    const userName = nameItem?.assigned_to_name?.trim() || userId;

    result.push({
      user_id: userId,
      user_name: userName,
      tasks_in_progress: inProgress,
      tasks_pending: pending,
      tasks_overdue: overdue,
      tasks_completed_today: completedToday,
      total_active: totalActive,
      avg_sla_minutes: avgSlaMins,
      sla_violated_pct: slaViolatedPct,
      bottleneck_score: Math.round(bottleneckScore * 10) / 10,
      load_status: loadStatus,
    });
  }

  // Default sort: most critical first (bottleneck score desc)
  return result.sort((a, b) => b.bottleneck_score - a.bottleneck_score);
}

/**
 * Compute team dashboard KPIs.
 */
export function computeTeamDashboard(
  items: UnifiedWorkItem[],
  members: TeamMemberWorkload[],
): TeamDashboardKPIs {
  const totalMembers = members.length || 1;
  const activeItems = items.filter(i => i.status !== 'completed');
  const overdueItems = items.filter(i => i.is_overdue && i.status !== 'completed');

  // Average completion time (from completed items with dates)
  const completedWithDates = items.filter(i =>
    i.status === 'completed' && i.completed_at && i.created_at
  );
  let avgTime: number | null = null;
  if (completedWithDates.length > 0) {
    const totalMs = completedWithDates.reduce((sum, i) => {
      const start = new Date(i.created_at).getTime();
      const end = new Date(i.completed_at!).getTime();
      return sum + (end - start);
    }, 0);
    avgTime = Math.round((totalMs / completedWithDates.length) / (1000 * 60 * 60 * 24) * 10) / 10;
  }

  return {
    tasks_per_employee: Math.round((activeItems.length / totalMembers) * 10) / 10,
    overdue_tasks: overdueItems.length,
    average_completion_time: avgTime,
    current_workload: activeItems.length,
    total_members: totalMembers,
    total_sla_violated: members.reduce((s, m) => s + Math.round(m.sla_violated_pct * m.total_active / 100), 0),
    avg_sla_compliance_pct: members.length > 0
      ? Math.round(members.reduce((s, m) => s + (100 - m.sla_violated_pct), 0) / members.length)
      : 100,
  };
}
